// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import * as React from "react";
import BackdropProgress from "../../BackdropProgress";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import Checkbox from "@mui/material/Checkbox";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ConfirmationDialog from "../dialog/ConfirmationDialog";
import CustomSnackbar from "../ui/Snackbars";
import Dialog from "@mui/material/Dialog";
import EnhancedTableHead from "../ui/EnhancedTableHead";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import Grid from "@mui/material/Grid";
import LinearProgress from "@mui/material/LinearProgress";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import Paper from "@mui/material/Paper";
import SearchIcon from "@mui/icons-material/Search";
import InfoIcon from "@mui/icons-material/Info";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useHttp from "../../utils/http";
import wso2Theme from "../../theme";
import {
  truncateString,
  getComparator,
  stableSort,
} from "../../utils/formatting";
import {
  patchApproveInvoices,
  patchRejectInvoices,
  postSearchTravelInvoices,
  postPushInvoices,
} from "../../utils/end-points";
import { isAccessGranted } from "../../utils/oauth";
import { makeStyles } from "@mui/styles";
import { Autocomplete } from "@mui/material";
import { PRIVILEGES } from "../../Config.js";
import { styled } from "@mui/material/styles";
import { invoiceApproval, invoiceApprovalLabels } from "../../utils/const";
import {
  getFinanceApprovalStatusInfo,
  getHrApprovalStatusInfo,
  getInvoiceStatusLabelColors,
  getNetSuiteStatusInfo,
} from "../../theme/status-colors";
import { useEffect, useState, useCallback } from "react";
import "../../css/AllInvoices.scss";

const useStyles = makeStyles(() => ({
  paper: { minWidth: "550px" },
}));

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#f5f5f9",
    color: "rgba(0, 0, 0, 0.87)",
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: "1px solid #dadde9",
  },
}));

const headCells = [
  { id: "index", label: "", align: "left", disablePadding: false },
  {
    id: "invoice_number",
    label: "Invoice Number",
    align: "left",
    disablePadding: false,
  },
  {
    id: "job_number",
    label: "Job Number",
    align: "left",
    disablePadding: false,
  },
  { id: "month", label: "Month", align: "left", disablePadding: false },
  { id: "cost_type", label: "Cost Type", align: "left", disablePadding: false },
  {
    id: "invoice_amount",
    label: "Invoice Amount",
    align: "left",
    disablePadding: false,
  },
  {
    id: "invoice_currency",
    label: "Invoice Currency",
    align: "left",
    disablePadding: false,
  },
  {
    id: "converted_amount",
    label: "Paid Amount",
    align: "left",
    disablePadding: false,
  },
  {
    id: "converted_currency",
    label: "Paid Currency",
    align: "left",
    disablePadding: false,
  },
  {
    id: "hr_approval",
    label: "HR Approval",
    align: "center",
    disablePadding: false,
  },
  {
    id: "finance_approval",
    label: "Finance Approval",
    align: "center",
    disablePadding: false,
  },
  {
    id: "net_suite_status",
    label: "NS Status",
    align: "center",
    disablePadding: false,
  },
];
const AllInvoices = () => {
  const classes = useStyles();
  const { handleRequest } = useHttp();
  const [isLoading, setLoading] = useState(false);
  const [isPush, setPush] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchFilter, setSearchFilter] = useState("");
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("invoice_number");
  const [page, setPage] = useState(0);
  const [hrApprovalFilter, setHrApprovalFilter] = useState(null);
  const [financeApprovalFilter, setFinanceApprovalFilter] = useState(null);
  const [netSuiteStatusFilter, setNetSuiteStatusFilter] = useState(null);
  const [isPageLoading, setPageLoading] = useState(true);
  const [isTableLoading, setTableLoading] = useState(false);
  const [isTablePaginating, setTablePaginating] = useState(false);
  const [showRejectReasonDialog, setShowRejectReasonDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState(null);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [snackbarData, setSnackbarData] = useState({
    open: false,
    message: "",
    severity: "",
    onClose: null,
  });
  const [confirmationDialogData, setConfirmationDialogData] = useState({
    open: false,
    title: "",
    content: "",
    acceptLabel: "",
    declineLabel: "",
    acceptAlert: null,
    declineAlert: null,
  });

  useEffect(() => {
    if (window.innerHeight >= 850) {
      setRowsPerPage(12);
    }
  }, []);

  useEffect(() => {
    fetchInvoices();
  }, [
    rowsPerPage,
    hrApprovalFilter,
    financeApprovalFilter,
    netSuiteStatusFilter,
    orderBy,
    order,
  ]);

  const fetchInvoices = useCallback(
    async (showSuccessSnack) => {
      setTableLoading(true);
      setSelectedInvoices([]);

      let invoiceStatus = getInvoiceStatusByFilters();
      let body = {
        limit: rowsPerPage,
        offset: 0,
        searchString: searchFilter,
        sortColumn: orderBy,
        sortOrder: order,
        status: invoiceStatus,
      };
      handleRequest(
        postSearchTravelInvoices,
        "POST",
        body,
        (invoices) => {
          if (invoices) {
            setInvoices(invoices);
            setTableLoading(false);
            setPage(0);
            if (showSuccessSnack) {
              setSnackbarData({
                message: "Success",
                open: true,
                severity: "success",
                onClose: onCloseSnackbar,
              });
            }
          } else {
            setSnackbarData({
              message: "Error while fetching invoices",
              open: true,
              severity: "error",
              onClose: onCloseSnackbar,
            });
          }
          setPageLoading(false);
        },
        (error) => {
          setPageLoading(false);
          setSnackbarData({
            message: error,
            open: true,
            severity: "error",
            onClose: onCloseSnackbar,
          });
        }
      );
    },
    [
      rowsPerPage,
      searchFilter,
      hrApprovalFilter,
      financeApprovalFilter,
      netSuiteStatusFilter,
      orderBy,
      order,
    ]
  );

  const loadMoreTravels = (offset) => {
    setTablePaginating(true);

    let invoiceStatus = getInvoiceStatusByFilters();
    let body = {
      limit: rowsPerPage,
      offset: offset,
      searchString: searchFilter,
      sortColumn: orderBy,
      sortOrder: order,
      status: invoiceStatus,
    };

    handleRequest(
      postSearchTravelInvoices,
      "POST",
      body,
      (response) => {
        if (response) {
          let invoiceList = invoices.concat(response);
          setInvoices(invoiceList);
          if (response.length === 0) {
            setPage((page) => page - 1);
            setSnackbarData({
              message: "No more data",
              open: true,
              severity: "success",
              onClose: onCloseSnackbar,
            });
          }
        } else {
          setSnackbarData({
            message: "Error while fetching invoices",
            open: true,
            severity: "error",
            onClose: onCloseSnackbar,
          });
        }
        setTablePaginating(false);
      },
      (error) => {
        setTablePaginating(false);
        setSnackbarData({
          message: error,
          open: true,
          severity: "error",
          onClose: onCloseSnackbar,
        });
      }
    );
  };

  const getInvoiceStatusByFilters = () => {
    if (netSuiteStatusFilter === invoiceApprovalLabels.PUSHED) {
      return invoiceApproval.COMPLETE;
    } else {
      if (financeApprovalFilter === invoiceApprovalLabels.APPROVED) {
        return invoiceApproval.ACCEPTED_BY_FINANCE;
      } else if (financeApprovalFilter === invoiceApprovalLabels.REJECTED) {
        return invoiceApproval.REJECTED_BY_FINANCE;
      } else {
        if (hrApprovalFilter === invoiceApprovalLabels.APPROVED) {
          return invoiceApproval.ACCEPTED_BY_TRAVEL;
        } else if (hrApprovalFilter === invoiceApprovalLabels.REJECTED) {
          return invoiceApproval.REJECTED_BY_TRAVEL;
        } else if (hrApprovalFilter === invoiceApprovalLabels.PENDING) {
          return invoiceApproval.PENDING;
        } else {
          return null;
        }
      }
    }
  };

  const handleHrApprovalFilter = (event, value) => {
    setHrApprovalFilter(value ? value : null);
  };

  const handleFinanceApprovalFilter = (event, value) => {
    setHrApprovalFilter(null);
    setFinanceApprovalFilter(value ? value : null);
  };

  const handleNetSuiteFilter = (event, value) => {
    setHrApprovalFilter(null);
    setFinanceApprovalFilter(null);
    setNetSuiteStatusFilter(value ? value : null);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPush(true);
    if (invoices.length <= newPage * rowsPerPage) {
      loadMoreTravels(invoices.length);
    }
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchFilter = (event) => {
    showPushButton();
    setSearchFilter(event.target.value);
    if (!event.target.value) {
      fetchInvoices();
    }
  };

  const onCloseSnackbar = () => {
    setSnackbarData({ ...snackbarData, open: false });
  };

  const isSelectDisabled = (status) => {
    if (isAccessGranted([PRIVILEGES.INVOICE_HR_APPROVAL])) {
      if (status === invoiceApproval.PENDING) {
        return false;
      }
      return true;
    } else if (isAccessGranted([PRIVILEGES.INVOICE_FINANCE_APPROVAL])) {
      if (status === invoiceApproval.ACCEPTED_BY_TRAVEL) {
        return false;
      }
      return true;
    }
    return true;
  };

  const isChecked = (row) => {
    if (selectedInvoices.includes(row.id)) {
      return true;
    }

    if (isAccessGranted([PRIVILEGES.INVOICE_HR_APPROVAL])) {
      if (row.status === invoiceApproval.PENDING) {
        return false;
      }
      return true;
    } else if (isAccessGranted([PRIVILEGES.INVOICE_FINANCE_APPROVAL])) {
      if (row.status === invoiceApproval.ACCEPTED_BY_TRAVEL) {
        return false;
      }
      return true;
    }
    return true;
  };

  const selectInvoice = (invoiceId) => {
    if (selectedInvoices.includes(invoiceId)) {
      setSelectedInvoices(
        selectedInvoices.filter((invoice) => invoice !== invoiceId)
      );
    } else {
      setSelectedInvoices((prev) => [...prev, invoiceId]);
    }
  };

  const getHrApprovalInfo = (invoice) => {
    let statusChip = getHrApprovalStatusInfo(invoice.status);
    return (
      <div>
        <span
          className="Status-chip"
          style={{
            backgroundColor: statusChip[1],
            color: statusChip[2],
          }}
        >
          {statusChip[0]}
        </span>
        <span className="Info-icon">
          {invoice.status === invoiceApproval.REJECTED_BY_TRAVEL && (
            <HtmlTooltip
              title={
                <>
                  <Typography variant="subtitle1" color="inherit">
                    Rejected Reason:
                  </Typography>
                  <Typography variant="subtitle2">
                    {invoice.rejectReason}
                  </Typography>
                </>
              }
            >
              <InfoIcon size="small" />
            </HtmlTooltip>
          )}
        </span>
      </div>
    );
  };

  const getFinanceApprovalInfo = (invoice) => {
    let statusChip = getFinanceApprovalStatusInfo(invoice.status);
    return (
      <span>
        <span
          className="Status-chip"
          style={{
            backgroundColor: statusChip[1],
            color: statusChip[2],
          }}
        >
          {statusChip[0]}
        </span>
        <span className="Info-icon">
          {invoice.status === invoiceApproval.REJECTED_BY_FINANCE && (
            <HtmlTooltip
              title={
                <>
                  <Typography variant="subtitle1" color="inherit">
                    Rejected Reason:
                  </Typography>
                  <Typography variant="subtitle2">
                    {invoice.rejectReason}
                  </Typography>
                </>
              }
            >
              <InfoIcon size="small" />
            </HtmlTooltip>
          )}
        </span>
      </span>
    );
  };

  const getNetSuiteStatus = (invoice) => {
    let statusChip = getNetSuiteStatusInfo(invoice.status);
    return (
      <span>
        <span
          className="Status-chip"
          style={{
            backgroundColor: statusChip[1],
            color: statusChip[2],
          }}
        >
          {statusChip[0]}
        </span>
      </span>
    );
  };

  const rejectInvoice = (event) => {
    if (rejectReason === null || rejectReason === "") {
      setSnackbarData({
        message: "Please enter a reject reason",
        open: true,
        severity: "error",
        onClose: onCloseSnackbar,
      });
    } else {
      setShowRejectReasonDialog(false);
      setPageLoading(true);
      let body = {
        invoiceIds: selectedInvoices,
        rejectReason: rejectReason,
      };
      handleRequest(
        patchRejectInvoices,
        "PATCH",
        body,
        (invoiceIds) => {
          if (invoiceIds) {
            setSelectedInvoices([]);
            fetchInvoices(true);
          } else {
            setSnackbarData({
              message: "Error while rejecting invoices",
              open: true,
              severity: "error",
              onClose: onCloseSnackbar,
            });
          }
        },
        (error) => {
          setSnackbarData({
            message: error,
            open: true,
            severity: "error",
            onClose: onCloseSnackbar,
          });
        }
      );
    }
  };

  const cancelInvoiceRejection = (event) => {
    setRejectReason("");
    setShowRejectReasonDialog(false);
  };

  const handleInvoice = (isApproved) => {
    var title = "Approve Invoice";
    var content =
      "You are about to approve " +
      selectedInvoices.length +
      " invoice(s). Are you sure you want to approve these invoice(s)?";

    if (isApproved) {
      setConfirmationDialogData({
        open: true,
        title: title,
        content: content,
        acceptLabel: "Yes",
        declineLabel: "No",
        acceptAlert: handleApproveInvoiceAlert,
        declineAlert: handleDeclineAlert,
      });
    } else {
      setShowRejectReasonDialog(true);
    }
  };

  const handleApproveInvoiceAlert = () => {
    setConfirmationDialogData((data) => ({ ...data, open: false }));
    setPageLoading(true);
    let body = {
      invoiceIds: selectedInvoices,
    };
    handleRequest(
      patchApproveInvoices,
      "PATCH",
      body,
      (invoiceIds) => {
        if (invoiceIds) {
          setSelectedInvoices([]);
          fetchInvoices(true);
        } else {
          setPageLoading(false);
          setSnackbarData({
            message: "Error while approving invoices",
            open: true,
            severity: "error",
            onClose: onCloseSnackbar,
          });
        }
      },
      (error) => {
        setPageLoading(false);
        setSnackbarData({
          message: error,
          open: true,
          severity: "error",
          onClose: onCloseSnackbar,
        });
      }
    );
  };

  const handleRejectInvoiceReason = (event) => {
    setRejectReason(event.target.value);
  };
  const handleDeclineAlert = () => {
    setConfirmationDialogData((data) => ({ ...data, open: false }));
  };
  const handlePushInvoicesToNS = () => {
    setConfirmationDialogData({
      open: true,
      title: "Push Invoice NS",
      content:
        "Are you sure you want to push approved invoices to Net Suite? Please note that if there are duplicate invoice numbers, only the first invoice will be pushed to Net Suite.",
      acceptLabel: "Yes",
      declineLabel: "No",
      acceptAlert: handlePushInvoices,
      declineAlert: handleDeclineAlert,
    });
  };

  const showPushButton = () => {
    if (invoices.length == 0) {
      setPush(true);
    }
    {
      stableSort(invoices, getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map((row, key) => {
          if (row.status === invoiceApproval.ACCEPTED_BY_FINANCE) {
            setPush(false);
          } else {
            setPush(true);
          }
        });
    }
  };

  useEffect(() => {
    if (invoices.length >= 0) {
      showPushButton();
    }
  }, [page, rowsPerPage, invoices, searchFilter]);

  const handlePushInvoices = () => {
    setConfirmationDialogData((data) => ({ ...data, open: false }));
    {
      let approvedInvoices = [];
      stableSort(invoices, getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map((row, key) => {
          setLoading(true);
          if (row.status === invoiceApproval.ACCEPTED_BY_FINANCE) {
            let invoice = {
              invoiceNumber: row.invoiceNumber,
              travelJobNumber: row.jobNumber,
            };
            approvedInvoices.push(invoice);
          }
        });
      let body = {
        invoices: approvedInvoices,
      };
      handleRequest(
        postPushInvoices,
        "POST",
        body,
        (invoiceResult) => {
          if (invoiceResult) {
            if (invoiceResult.failedInvoices?.length > 0) {
              setSnackbarData({
                message: `Error while pushing following invoices to Net Suite: 
                                ${invoiceResult.failedInvoices
                                  .map((item) => item.invoiceNumber)
                                  .join(", ")}`,
                open: true,
                severity: "error",
                onClose: onCloseSnackbar,
              });
              fetchInvoices(false);
            } else {
              fetchInvoices(true);
            }
            setLoading(false);
          } else {
            setLoading(false);
            setSnackbarData({
              message: "Error while pushing invoices to Net Suite",
              open: true,
              severity: "error",
              onClose: onCloseSnackbar,
            });
          }
        },
        (error) => {
          setLoading(false);
          setSnackbarData({
            message: error,
            open: true,
            severity: "error",
            onClose: onCloseSnackbar,
          });
        }
      );
    }
    showPushButton();
  };
  return (
    <React.Fragment>
      <CustomSnackbar snackbar={snackbarData} />
      <BackdropProgress open={isPageLoading || isLoading} />
      <ConfirmationDialog confirmationDialogData={confirmationDialogData} />

      <div className="All-invoices">
        <Dialog
          onClose={() => setShowRejectReasonDialog(false)}
          open={showRejectReasonDialog}
          classes={{ paper: classes.paper }}
          className="Reject-reason-dialog"
        >
          <div className="Dialog-div">
            <Typography variant="h4" gutterBottom className="Title">
              Reject Invoice
            </Typography>

            <Typography gutterBottom className="Description">
              You are about to reject <span>{selectedInvoices.length}</span>{" "}
              invoice(s). Please make sure you want to reject these invoice(s).
              This can not be undone.
            </Typography>

            <TextField
              size="small"
              fullWidth
              placeholder="Reason to reject"
              className="Textfield"
              onChange={handleRejectInvoiceReason}
            />

            <Grid item lg={12} className="Button-div">
              <Button
                variant="contained"
                color="secondary"
                className="Reject-button"
                onClick={rejectInvoice}
              >
                Reject
              </Button>
              <Button
                variant="outlined"
                color="primary"
                className="Cancel-button"
                onClick={cancelInvoiceRejection}
              >
                Cancel
              </Button>
            </Grid>
          </div>
        </Dialog>

        <div className="Page-title">All Invoices</div>

        {!isPageLoading && (
          <Grid container>
            <Grid item lg={12} className="Table-grid">
              <Paper className="Border-elevation">
                <div
                  className="Search-and-filters"
                  style={{ borderColor: wso2Theme.palette.primary.light }}
                >
                  <div className="Table-search-field">
                    <TextField
                      variant="outlined"
                      label="Search"
                      size="small"
                      className="Search-text-fields"
                      onChange={handleSearchFilter}
                    />
                    <Button
                      variant={"contained"}
                      startIcon={<SearchIcon />}
                      color="secondary"
                      className="Filter-button"
                      onClick={fetchInvoices}
                    >
                      Search
                    </Button>
                    <Button
                      variant={filtersExpanded ? "contained" : "outlined"}
                      startIcon={<FilterAltIcon />}
                      color="primary"
                      className="Filter-button"
                      onClick={() => setFiltersExpanded(!filtersExpanded)}
                    >
                      {filtersExpanded ? "Hide Filters" : "Show Filters"}
                    </Button>
                  </div>
                  {filtersExpanded && (
                    <div className="Filters-div">
                      <Grid container>
                        <Grid
                          item
                          lg={3}
                          className="Filter-grid-item-hr-approval"
                        >
                          <Autocomplete
                            size="small"
                            disablePortal
                            options={[
                              invoiceApprovalLabels.PENDING,
                              invoiceApprovalLabels.APPROVED,
                              invoiceApprovalLabels.REJECTED,
                            ]}
                            value={hrApprovalFilter}
                            getOptionLabel={(status) => status}
                            onChange={handleHrApprovalFilter}
                            renderOption={(props, status) => (
                              <Box {...props}>
                                <span
                                  className="Status-dot-dropdown"
                                  style={{
                                    backgroundColor:
                                      getInvoiceStatusLabelColors(status),
                                  }}
                                ></span>
                                {status}
                              </Box>
                            )}
                            disabled={
                              financeApprovalFilter || netSuiteStatusFilter
                            }
                            renderInput={(params) => (
                              <TextField {...params} label="HR Approval" />
                            )}
                          />
                        </Grid>
                        <Grid
                          item
                          lg={3}
                          className="Filter-grid-item-hr-approval"
                        >
                          <Autocomplete
                            size="small"
                            disablePortal
                            options={[
                              invoiceApprovalLabels.APPROVED,
                              invoiceApprovalLabels.REJECTED,
                            ]}
                            value={financeApprovalFilter}
                            getOptionLabel={(status) => status}
                            onChange={handleFinanceApprovalFilter}
                            renderOption={(props, status) => (
                              <Box {...props}>
                                <span
                                  className="Status-dot-dropdown"
                                  style={{
                                    backgroundColor:
                                      getInvoiceStatusLabelColors(status),
                                  }}
                                ></span>
                                {status}
                              </Box>
                            )}
                            disabled={netSuiteStatusFilter}
                            renderInput={(params) => (
                              <TextField {...params} label="Finance Approval" />
                            )}
                          />
                        </Grid>
                        <Grid
                          item
                          lg={3}
                          className="Filter-grid-item-hr-approval"
                        >
                          <Autocomplete
                            size="small"
                            disablePortal
                            options={[invoiceApprovalLabels.PUSHED]}
                            value={netSuiteStatusFilter}
                            getOptionLabel={(status) => status}
                            onChange={handleNetSuiteFilter}
                            renderOption={(props, status) => (
                              <Box {...props}>
                                <span
                                  className="Status-dot-dropdown"
                                  style={{
                                    backgroundColor:
                                      getInvoiceStatusLabelColors(status),
                                  }}
                                ></span>
                                {status}
                              </Box>
                            )}
                            renderInput={(params) => (
                              <TextField {...params} label="NetSuite Status" />
                            )}
                          />
                        </Grid>
                      </Grid>
                    </div>
                  )}
                </div>

                {isAccessGranted([
                  PRIVILEGES.INVOICE_FINANCE_APPROVAL,
                  PRIVILEGES.INVOICE_HR_APPROVAL,
                ]) && (
                  <Grid container className="Button-grid-approvals">
                    <Grid item lg={10}>
                      {isAccessGranted([PRIVILEGES.INVOICE_HR_APPROVAL]) ? (
                        <>
                          <Button
                            variant="contained"
                            size="small"
                            color="secondary"
                            className="Approve-button"
                            startIcon={<CheckCircleOutlineIcon />}
                            disabled={selectedInvoices.length === 0}
                            onClick={() => handleInvoice(true)}
                          >
                            Approve (HR)
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            color="primary"
                            className="Reject-button"
                            startIcon={<HighlightOffIcon />}
                            disabled={selectedInvoices.length === 0}
                            onClick={() => handleInvoice(false)}
                          >
                            Reject (HR)
                          </Button>
                        </>
                      ) : isAccessGranted([
                          PRIVILEGES.INVOICE_FINANCE_APPROVAL,
                        ]) ? (
                        <>
                          <Button
                            variant="contained"
                            size="small"
                            color="secondary"
                            className="Approve-button"
                            startIcon={<CheckCircleOutlineIcon />}
                            disabled={selectedInvoices.length === 0}
                            onClick={() => handleInvoice(true)}
                          >
                            Approve (Finance)
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            color="primary"
                            className="Reject-button"
                            startIcon={<HighlightOffIcon />}
                            disabled={selectedInvoices.length === 0}
                            onClick={() => handleInvoice(false)}
                          >
                            Reject (Finance)
                          </Button>
                        </>
                      ) : (
                        <></>
                      )}
                    </Grid>
                    <Grid item lg={2} className="Button-grid-netsuite">
                      {isAccessGranted([PRIVILEGES.PUSH_INVOICES_TO_NS]) && (
                        <Button
                          style={{ marginLeft: 10 }}
                          variant="contained"
                          size="small"
                          color="secondary"
                          className="Netsuite-button"
                          startIcon={<CurrencyExchangeIcon />}
                          disabled={isPush}
                          onClick={handlePushInvoicesToNS}
                        >
                          Push Invoices to NS
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                )}

                {isTableLoading && <LinearProgress color="secondary" />}
                <TableContainer>
                  <Table size={"small"}>
                    <EnhancedTableHead
                      headCells={headCells}
                      order={order}
                      orderBy={orderBy}
                      onRequestSort={handleRequestSort}
                    />
                    <TableBody>
                      {stableSort(invoices, getComparator(order, orderBy))
                        .slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage
                        )
                        .map((row, key) => {
                          return (
                            <TableRow
                              hover
                              tabIndex={-1}
                              className="Table-row"
                              key={key}
                            >
                              <TableCell
                                className="Table-cell Table-cell-checkbox"
                                padding="checkbox"
                              >
                                <Checkbox
                                  color="secondary"
                                  onChange={() => selectInvoice(row.id)}
                                  disabled={isSelectDisabled(row.status)}
                                  checked={isChecked(row)}
                                />
                              </TableCell>
                              <TableCell className="Table-cell Table-cell-invoice-number">
                                {truncateString(row.invoiceNumber, 20)}
                              </TableCell>
                              <TableCell className="Table-cell Table-cell-job-number">
                                {truncateString(row.jobNumber, 20)}
                              </TableCell>
                              <TableCell className="Table-cell Table-cell">
                                {truncateString(row.month, 20)}
                              </TableCell>
                              <TableCell className="Table-cell Table-cell-cost-type">
                                {truncateString(row.costTypeName, 20)}
                              </TableCell>
                              <TableCell className="Table-cell Table-cell-amount">
                                {truncateString(row.invoiceAmount, 20)}
                              </TableCell>
                              <TableCell className="Table-cell">
                                {truncateString(row.invoiceCurrency, 20)}
                              </TableCell>
                              <TableCell className="Table-cell Table-cell-amount">
                                {truncateString(row.convertedAmount, 20)}
                              </TableCell>
                              <TableCell className="Table-cell">
                                {truncateString(row.convertedCurrency, 20)}
                              </TableCell>
                              <TableCell className="Table-cell Table-cell-hr-approval">
                                {getHrApprovalInfo(row)}
                              </TableCell>
                              <TableCell className="Table-cell Table-cell-finance-approval">
                                {getFinanceApprovalInfo(row)}
                              </TableCell>
                              <TableCell className="Table-cell Table-cell-net-suite-status">
                                {getNetSuiteStatus(row)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {invoices.length === 0 && (
                  <div className="No-invoices-label">No Invoices</div>
                )}
                {isTablePaginating && <LinearProgress color="secondary" />}

                <TablePagination
                  className="Table-pagination"
                  rowsPerPageOptions={
                    window.innerHeight >= 850 ? [5, 12, 15] : [5, 10, 15]
                  }
                  component="div"
                  count={-1}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Paper>
            </Grid>
          </Grid>
        )}
      </div>
    </React.Fragment>
  );
};

export default AllInvoices;
